'use client';

import { useMemo, useState } from 'react';

import { IMasterclass } from '@/entities/masterclass/types';
import { sortMasterclasses } from '@/widgets/masterclasses/utils/sort-masterclasses';

/**
 * Читаем query только на клиенте (без next/navigation useSearchParams) —
 * этот хук иначе форсирует bailout SSR всего дерева в Suspense
 * (https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout).
 */
const getInitialQueryValue = (key: string, fallback: string): string => {
  if (typeof window === 'undefined') {
    return fallback;
  }
  return new URLSearchParams(window.location.search).get(key) ?? fallback;
};

const updateUrl = ({ value, key }: { value: string; key: string }) => {
  const updatedSearchParams = new URLSearchParams(window.location.search);
  if (!value) {
    updatedSearchParams.delete(key);
  } else {
    updatedSearchParams.set(key, value);
  }
  window.history.replaceState(null, '', '?' + updatedSearchParams.toString());
};

export const useGetFilteredMcs = (
  masterclasses: IMasterclass[],
  initialCategoryId: string = 'all',
) => {
  const [categoryId, setCategoryId] = useState<string>(() =>
    getInitialQueryValue('category', initialCategoryId),
  );
  const [sortValue, setSortValue] = useState(() =>
    getInitialQueryValue('sort', 'default'),
  );
  const [searchValue, setSearchValue] = useState(() =>
    getInitialQueryValue('search', ''),
  );

  const onCategoryChange = (value: string) => {
    setCategoryId(value);
    updateUrl({ value, key: 'category' });
  };

  const onSearchChange = (value: string) => {
    setSearchValue(value);
    updateUrl({ value, key: 'search' });
  };

  const onSortChange = (value: string) => {
    setSortValue(value);
    updateUrl({ value, key: 'sort' });
  };

  const sortedMasterclasses = useMemo(() => {
    if (!masterclasses) {
      return [];
    }
    return sortMasterclasses(masterclasses, sortValue);
  }, [masterclasses, sortValue]);

  const searchMasterclasses = useMemo(() => {
    if (!sortedMasterclasses) {
      return [];
    }
    if (!searchValue) {
      return sortedMasterclasses;
    }
    return sortedMasterclasses?.filter((masterclass) => {
      if (masterclass.name.toLowerCase().includes(searchValue.toLowerCase())) {
        return true;
      }
      return false;
    });
  }, [sortedMasterclasses, searchValue]);

  const searchMasterclassesByCategory = useMemo(() => {
    if (!searchMasterclasses) {
      return [];
    }
    if (!categoryId || categoryId === 'all') {
      return searchMasterclasses;
    }
    return searchMasterclasses.filter((masterclass) => {
      return masterclass.categories?.some(
        (category) => category.id === +categoryId,
      );
    });
  }, [searchMasterclasses, categoryId]);

  return useMemo(() => {
    return {
      masterclasses: searchMasterclassesByCategory,
      onCategoryChange,
      onSearchChange,
      searchValue,
      sortValue,
      categoryId,
      onSortChange,
    };
  }, [
    searchMasterclassesByCategory,
    onCategoryChange,
    onSearchChange,
    searchValue,
    sortValue,
    categoryId,
    onSortChange,
  ]);
};
